// src/services/api.js
import axios from 'axios';
import { tokenManager } from './TokenManager';

import * as Sentry from "@sentry/react";

const API_BASE_URL = 'https://barrack.ai';

const WS_URL = process.env.REACT_APP_WS_URL;

// Error Categories (matching backend approach)
const ErrorCategory = {
  PUBLIC: "public",      // Safe to show users (validation, permissions)
  PRIVATE: "private",    // Hide details, show generic message (internal errors)
  UPSTREAM: "upstream"   // API/service errors that need translation
};

// Comprehensive error codes (matching backend ERROR_CODES)
const ERROR_CODES = {
  // Authentication & Authorization
  'AUTH_ERROR': 'AUTH_ERROR',
  'ACCESS_DENIED': 'ACCESS_DENIED',
  'USER_NOT_FOUND': 'USER_NOT_FOUND',
  'INVALID_USER_PROFILE': 'INVALID_USER_PROFILE',
  'INCOMPLETE_PROFILE': 'INCOMPLETE_PROFILE',
  'INVALID_CREDENTIALS': 'INVALID_CREDENTIALS',
  'ACCOUNT_LOCKED': 'ACCOUNT_LOCKED',
  'EMAIL_ALREADY_REGISTERED': 'EMAIL_ALREADY_REGISTERED',
  'SESSION_EXPIRED': 'SESSION_EXPIRED',
  'TOKEN_BLACKLISTED': 'TOKEN_BLACKLISTED',
  'AUTO_LOGIN_FAILED': 'AUTO_LOGIN_FAILED',
  'AUTHENTICATION_INTERRUPTED': 'AUTHENTICATION_INTERRUPTED',
  'AUTHENTICATION_FAILED': 'AUTHENTICATION_FAILED',
  'VERIFICATION_FAILED': 'VERIFICATION_FAILED',
  'MISSING_EMAIL': 'MISSING_EMAIL',
  'EMAIL_NOT_FOUND': 'EMAIL_NOT_FOUND',
  'INVALID_OTP': 'INVALID_OTP',
  'OTP_GENERATION_FAILED': 'OTP_GENERATION_FAILED',
  'EMAIL_SEND_FAILED': 'EMAIL_SEND_FAILED',
  'UNAUTHORIZED_ACCESS': 'UNAUTHORIZED_ACCESS',
  
  // Validation Errors
  'VALIDATION_ERROR': 'VALIDATION_ERROR',
  'INVALID_JSON': 'INVALID_JSON',
  'MISSING_FIELDS': 'MISSING_FIELDS',
  'INVALID_PARAMETERS': 'INVALID_PARAMETERS',
  'INVALID_ID_FORMAT': 'INVALID_ID_FORMAT',
  'INVALID_DATA': 'INVALID_DATA',
  'INVALID_REQUEST': 'INVALID_REQUEST',
  'MISSING_PARAMETER': 'MISSING_PARAMETER',
  'INVALID_PAGINATION': 'INVALID_PAGINATION',
  'INVALID_DATE_RANGE': 'INVALID_DATE_RANGE',
  'MISSING_TOKEN': 'MISSING_TOKEN',
  'INVALID_ACTION': 'INVALID_ACTION',
  'METHOD_NOT_ALLOWED': 'METHOD_NOT_ALLOWED',
  'INCOMPLETE_TOKENS': 'INCOMPLETE_TOKENS',
  'DATA_ERROR': 'DATA_ERROR',
  'CONFIG_ERROR': 'CONFIG_ERROR',
  'INVALID_RESPONSE': 'INVALID_RESPONSE',
  'INVALID_VM_PARAMETERS': 'INVALID_VM_PARAMETERS',
  'CLOUD_INIT_VALIDATION_FAILED': 'CLOUD_INIT_VALIDATION_FAILED',
  'REQUEST_TOO_LARGE': 'REQUEST_TOO_LARGE',
  'UNEXPECTED_PAYLOAD': 'UNEXPECTED_PAYLOAD',
  
  // Resource Errors
  'RESOURCE_NOT_FOUND': 'RESOURCE_NOT_FOUND',
  'RESOURCE_ALREADY_EXISTS': 'RESOURCE_ALREADY_EXISTS',
  'RESOURCE_IN_USE': 'RESOURCE_IN_USE',
  'RESOURCE_UNAVAILABLE': 'RESOURCE_UNAVAILABLE',
  
  // VM Specific
  'VM_NOT_FOUND': 'VM_NOT_FOUND',
  'VM_NOT_ACTIVE': 'VM_NOT_ACTIVE',
  'VM_UNAVAILABLE': 'VM_UNAVAILABLE',
  'VM_LIST_FAILED': 'VM_LIST_FAILED',
  'VM_NOT_COMPATIBLE': 'VM_NOT_COMPATIBLE',
  'VM_ALREADY_ATTACHED': 'VM_ALREADY_ATTACHED',
  'VM_NOT_ATTACHED': 'VM_NOT_ATTACHED',
  'VM_EVENTS_FAILED': 'VM_EVENTS_FAILED',
  'VM_FIREWALLS_FAILED': 'VM_FIREWALLS_FAILED',
  'CURRENCY_CONTEXT_MISMATCH': 'CURRENCY_CONTEXT_MISMATCH',
  'VM_NOT_FOUND_OR_FORBIDDEN': 'VM_NOT_FOUND_OR_FORBIDDEN',
  'VM_RESIZE_ERROR': 'VM_RESIZE_ERROR',
  'VM_START_ERROR': 'VM_START_ERROR',
  'VM_STOP_ERROR': 'VM_STOP_ERROR',
  'VM_REBOOT_ERROR': 'VM_REBOOT_ERROR',
  'VM_DELETE_ERROR': 'VM_DELETE_ERROR',
  'INVALID_VM_STATE': 'INVALID_VM_STATE',
  'NOT_FOUND_OR_FORBIDDEN': 'NOT_FOUND_OR_FORBIDDEN',
  
  // Deployment Specific
  'DEPLOYMENT_ERROR': 'DEPLOYMENT_ERROR',
  'FLAVOR_NOT_FOUND': 'FLAVOR_NOT_FOUND',
  'INVALID_FLAVOR': 'INVALID_FLAVOR',
  'STOCK_UNAVAILABLE': 'STOCK_UNAVAILABLE',
  'GPU_NOT_AVAILABLE': 'GPU_NOT_AVAILABLE',
  'VM_DEPLOY_FAILED': 'VM_DEPLOY_FAILED',
  'CALLBACK_REGISTRATION_FAILED': 'CALLBACK_REGISTRATION_FAILED',
  'OS_IMAGE_REQUIRED': 'OS_IMAGE_REQUIRED',
  'SSH_KEY_REQUIRED': 'SSH_KEY_REQUIRED',
  'ADMIN_PASSWORD_REQUIRED': 'ADMIN_PASSWORD_REQUIRED',
  
  // API Key Specific
  'DUPLICATE_NAME': 'DUPLICATE_NAME',
  'LIMIT_EXCEEDED': 'LIMIT_EXCEEDED',
  'KEY_GENERATION_FAILED': 'KEY_GENERATION_FAILED',
  'KEY_NOT_FOUND': 'KEY_NOT_FOUND',
  'API_KEYS_FETCH_FAILED': 'API_KEYS_FETCH_FAILED',
  'API_KEY_DELETE_FAILED': 'API_KEY_DELETE_FAILED',
  
  // Profile Specific
  'PROFILE_FETCH_FAILED': 'PROFILE_FETCH_FAILED',
  'PROFILE_UPDATE_FAILED': 'PROFILE_UPDATE_FAILED',
  'PROFILE_COMPLETENESS_CHECK_FAILED': 'PROFILE_COMPLETENESS_CHECK_FAILED',
  'COUNTRY_REQUIRED': 'COUNTRY_REQUIRED',
  
  // Credit & Billing
  'INSUFFICIENT_CREDITS': 'INSUFFICIENT_CREDITS',
  'PAYMENT_REQUIRED': 'PAYMENT_REQUIRED',
  'CURRENCY_NOT_SET': 'CURRENCY_NOT_SET',
  'INVALID_CURRENCY': 'INVALID_CURRENCY',
  
  // Generic Errors
  'SERVICE_UNAVAILABLE': 'SERVICE_UNAVAILABLE',
  'SERVER_ERROR': 'SERVER_ERROR',
  'API_CONNECTION_ERROR': 'API_CONNECTION_ERROR',
  'SERVICE_CONFIG_ERROR': 'SERVICE_CONFIG_ERROR',
  'INTERNAL_ERROR': 'INTERNAL_ERROR',
  'SERVICE_ERROR': 'SERVICE_ERROR',
  'NETWORK_ERROR': 'NETWORK_ERROR',
  'DATABASE_ERROR': 'DATABASE_ERROR',
  'RATE_LIMIT_EXCEEDED': 'RATE_LIMIT_EXCEEDED'
};

// User-friendly error messages (matching backend PUBLIC_ERROR_MESSAGES)

// Private error messages (hide internal details)
const PRIVATE_ERROR_MESSAGE = "An internal error occurred. Please try again later";
const UPSTREAM_ERROR_MESSAGE = "Service is temporarily unavailable. Please try again later";

// Enhanced Error Processing Class
class ErrorProcessor {
  // Categorize error codes (matching backend approach)
// Categorize error codes (matching backend approach)


static categorizeError(errorCode, httpStatus = null) {
  // Public errors - safe to show users (matches backend exactly)
  const publicErrors = new Set([
    // Authentication & Authorization
    'AUTH_ERROR', 'AUTH_SERVICE_ERROR', 'ACCESS_DENIED', 'USER_NOT_FOUND', 'INVALID_USER_PROFILE',
    
    'INCOMPLETE_PROFILE', 'INVALID_CREDENTIALS', 'ACCOUNT_LOCKED', 
    'EMAIL_ALREADY_REGISTERED', 'SESSION_EXPIRED', 'TOKEN_BLACKLISTED', 
    'AUTO_LOGIN_FAILED', 'AUTHENTICATION_INTERRUPTED', 'AUTHENTICATION_FAILED', 
    'VERIFICATION_FAILED', 'MISSING_EMAIL', 'EMAIL_NOT_FOUND', 'INVALID_OTP',
    'OTP_GENERATION_FAILED', 'EMAIL_SEND_FAILED', 'UNAUTHORIZED_ACCESS',
    'USER_PROFILE_ERROR', 'PASSWORD_REQUIREMENTS_NOT_MET', 'USER_CREATE_ERROR',
    'PASSWORD_RESET_FAILED', 'LOGIN_FAILED', 'ACCOUNT_SETUP_FAILED',
    'ACCOUNT_CREATION_FAILED', 'ACCOUNT_UPDATE_FAILED', 'PASSWORD_RESET_SUCCESS',
    'PASSWORD_RESET_SUCCESS_WITH_LOGIN', 'LOGIN_SUCCESS', 'REGISTRATION_ERROR',
    'SESSION_ERROR', 'SESSION_CHECK_ERROR', 'LOGOUT_ERROR', 'OTP_SENT',
    'EMAIL_SENT', 'VERIFICATION_NEEDED', 'VERIFIED',
    
    // OAuth & Social Authentication
    'OAUTH_INITIALIZATION_ERROR', 'OAUTH_CONFIG_ERROR', 'GOOGLE_CONNECTION_ERROR',
    'GOOGLE_SIGNIN_FAILED', 'GOOGLE_INFO_ERROR', 'GOOGLE_INFO_RETRIEVAL_FAILED',
    'USER_SEARCH_ERROR',
      'REGION_NOT_SUPPORTED',
  'VOLUME_ID_NOT_FOUND',
  'SCRIPT_ID_VALIDATION_FAILED',
  'COST_CALCULATION_FAILED',
  'VOLUME_SIZE_TOO_SMALL',
  'VM_NAME_ALREADY_EXISTS',
  'UNEXPECTED_RESPONSE',
  'VOLUME_NOT_DETACHED',
  'BOOTABLE_VOLUME_NOT_AVAILABLE',
  'VOLUME_STATUS_NOT_AVAILABLE',
  'VOLUME_NOT_ATTACHED',
    // Validation Errors
    'VALIDATION_ERROR', 'INVALID_JSON', 'MISSING_FIELDS', 'INVALID_PARAMETERS',
    'INVALID_ID_FORMAT', 'INVALID_DATA', 'INVALID_REQUEST', 'MISSING_PARAMETER',
    'INVALID_PAGINATION', 'INVALID_DATE_RANGE', 'MISSING_TOKEN', 'INVALID_ACTION',
    'METHOD_NOT_ALLOWED', 'INCOMPLETE_TOKENS', 'DATA_ERROR', 'CONFIG_ERROR',
    'INVALID_RESPONSE', 'INVALID_VM_PARAMETERS', 'CLOUD_INIT_VALIDATION_FAILED',
    'UNEXPECTED_PAYLOAD', 'MISSING_REQUEST_BODY', 'INVALID_DATE', 'INVALID_STATUS',
    
    // Resource Errors
    'RESOURCE_NOT_FOUND', 'RESOURCE_ALREADY_EXISTS', 'RESOURCE_IN_USE',
    'RESOURCE_UNAVAILABLE',
    
    // Volume Errors
    'VOLUME_NOT_FOUND', 'VOLUME_ALREADY_EXISTS', 'VOLUME_SIZE_INVALID',
    'VOLUME_IN_USE', 'VOLUME_CREATE_FAILED', 'VOLUME_DELETE_FAILED',
    'VOLUME_ATTACH_FAILED', 'VOLUME_DETACH_FAILED', 'VOLUME_RESIZE_FAILED',
    'VOLUME_CLONE_FAILED', 'UNSUPPORTED_OPERATION', 'CROSS_REGION_CLONE_NOT_SUPPORTED',
    'MIXED_VOLUME_TIERS', 'CROSS_TIER_NOT_ALLOWED', 'INVALID_VOLUME_NAME',
    'INVALID_VOLUME_TYPE', 'ID_GENERATION_ERROR', 'FEATURE_NOT_SUPPORTED',
    'MAPPING_ERROR', 'VOLUME_CREATION_ERROR',
    
    // Volume Creation Validation Errors
    'VOLUMES_INVALID_FORMAT', 'VOLUME_NAME_MISSING', 'VOLUME_SIZE_MISSING',
    'VOLUME_TYPE_MISSING', 'VOLUME_UNKNOWN_FIELD', 'VOLUME_NAME_TOO_SHORT',
    'VOLUME_NAME_TOO_LONG', 'VOLUME_NAME_INVALID_CHARS', 'VOLUME_NAME_INVALID_FORMAT',
    'DUPLICATE_VOLUME_NAME', 'VOLUME_NAME_EXISTS', 'HDD_VOLUME_SIZE_EXCEEDED',
    'NVME_VOLUME_SIZE_EXCEEDED',
    
    // Existing Volume Attachment Errors
    'EXISTING_VOLUMES_INVALID_FORMAT', 'TOO_MANY_EXISTING_VOLUMES',
    'EXISTING_VOLUME_INVALID_FORMAT', 'EXISTING_VOLUME_LEGACY_REJECTED',
    'DUPLICATE_EXISTING_VOLUME', 'EXISTING_VOLUME_NOT_FOUND',
    'BOOTABLE_VOLUME_NOT_ALLOWED', 'EXISTING_VOLUME_NOT_AVAILABLE',
    'EXISTING_VOLUME_WRONG_REGION',
    
    // Volume ID Validation Errors
    'VOLUME_ID_INVALID_FORMAT', 'VOLUME_ID_LEGACY_REJECTED',
    'VOLUME_ID_NOT_BOOTABLE', 'VOLUME_ID_NOT_AVAILABLE', 'VOLUME_ID_WRONG_REGION',
    
    // Volume Attachment Specific
    'TOO_MANY_VOLUMES', 'DUPLICATE_VOLUMES', 'ID_RESOLUTION_ERROR',
    'VM_STATUS_CHECK_FAILED', 'UNKNOWN_TIER', 'VOLUME_DETAILS_FAILED',
    'REGION_LOOKUP_FAILED',
    
    // Snapshot Errors
    'SNAPSHOT_NOT_FOUND', 'SNAPSHOT_ALREADY_EXISTS', 'SNAPSHOT_CREATE_FAILED',
    'SNAPSHOT_DELETE_FAILED', 'SNAPSHOT_RESTORE_FAILED', 'SNAPSHOT_LIST_FAILED',
    'SNAPSHOT_DETAILS_FAILED', 'INVALID_SNAPSHOT_PARAMETERS', 'SNAPSHOT_SIZE_INVALID',
    'SNAPSHOT_IN_USE', 'SNAPSHOT_PERMISSION_DENIED', 'SNAPSHOT_DELETED',
    'SNAPSHOT_STATUS_NOT_DELETABLE', 'DUPLICATE_SNAPSHOT_NAME', 'SNAPSHOT_IN_PROGRESS',
    'QUOTA_EXCEEDED', 'INSUFFICIENT_STORAGE', 'INVALID_SNAPSHOT_NAME',
    'SERVICE_MAINTENANCE', 'INVALID_DATE_FORMAT', 'INVALID_SIZE_FILTER',
    'INVALID_MIN_MINUTES', 'INVALID_MAX_MINUTES', 'INVALID_MINUTE_RANGE',
    'INVALID_MIN_COST', 'INVALID_MAX_COST', 'INVALID_SNAPSHOT_ID_FORMAT',
    
    // Image Errors
    'IMAGE_NOT_FOUND', 'IMAGE_ALREADY_EXISTS', 'IMAGE_CREATE_FAILED',
    'IMAGE_DELETE_FAILED', 'IMAGE_LIST_FAILED', 'IMAGE_DETAILS_FAILED',
    'INVALID_IMAGE_PARAMETERS', 'INVALID_CONTRACT_ID', 'INVALID_IMAGE_NAME',
    'IMAGE_ALREADY_EXISTS_FOR_SNAPSHOT', 'IMAGE_API_ERROR',
    
    // VM Errors
    'VM_NOT_FOUND', 'VM_NOT_ACTIVE', 'VM_UNAVAILABLE', 'VM_LIST_FAILED',
    'VM_NOT_COMPATIBLE', 'VM_ALREADY_ATTACHED', 'VM_NOT_ATTACHED',
    'VM_EVENTS_FAILED', 'VM_FIREWALLS_FAILED', 'CURRENCY_CONTEXT_MISMATCH',
    'VM_NOT_FOUND_OR_FORBIDDEN', 'VM_RESIZE_ERROR', 'VM_START_ERROR',
    'VM_STOP_ERROR', 'VM_REBOOT_ERROR', 'VM_DELETE_ERROR', 'INVALID_VM_STATE',
    'NOT_FOUND_OR_FORBIDDEN', 'VM_NOT_SHUTOFF', 'VM_NAME_NOT_AVAILABLE',
    'VM_PERMISSION_DENIED',
    
    // VM Status Validation Errors
    'VM_STATUS_NOT_HIBERNATABLE', 'VM_STATUS_NOT_STARTABLE',
    'VM_STATUS_NOT_DELETABLE', 'VM_STATUS_NOT_STOPPABLE', 'VM_STATUS_NOT_REBOOTABLE',
    
    // ID Format Validation Errors
    'INVALID_VM_ID_FORMAT', 'INVALID_BARRACK_ID_FORMAT',
    
    // Tier/Feature Support Errors
    'TIER_NOT_SUPPORTED',
    
    // Query/Parameter Validation Errors
    'QUERY_PARAMETER_ERROR', 'INVALID_MINUTES_RANGE', 'INVALID_COST_RANGE',
    
    // API-Specific Errors
    'VM_DETAILS_API_ERROR', 'VM_SERVICE_CONNECTION_ERROR',
    
    // Firewall Errors
    'FIREWALL_NOT_FOUND', 'FIREWALL_ALREADY_EXISTS', 'FIREWALL_CREATE_FAILED',
    'FIREWALL_DELETE_FAILED', 'FIREWALL_LIST_FAILED', 'FIREWALL_DETAILS_FAILED',
    'FIREWALL_ATTACH_FAILED', 'FIREWALL_DETACH_FAILED', 'FIREWALL_LIMIT_EXCEEDED',
    'FIREWALL_HAS_ATTACHMENTS', 'FIREWALL_IN_USE', 'FIREWALL_RULE_NOT_FOUND',
    'FIREWALL_RULE_ALREADY_EXISTS', 'FIREWALL_RULE_ADD_FAILED',
    'FIREWALL_RULE_DELETE_FAILED', 'INVALID_RULE_PARAMETERS', 'RULE_QUOTA_EXCEEDED',
    'DEFAULT_RULE_DELETE_FORBIDDEN', 'SECURITY_RULE_ADD_FAILED',
    'SECURITY_RULE_DELETE_FAILED', 'RULE_NOT_FOUND', 'SECURITY_RULE_APPLICATION_FAILED',
    'SECURITY_RULE_REMOVE_FAILED', 'SECURITY_RULE_ALREADY_EXISTS',
    
    'REGION_MISMATCH', 'REGION_NOT_AVAILABLE', 'ATTACHMENT_LIMIT_EXCEEDED',
    'REGION_CAPACITY_EXCEEDED', 'INVALID_REGION', 'UNSUPPORTED_REGION',
    'INVALID_REGION_GPU_COMBINATION', 'SERVICE_INITIALIZATION_ERROR',
    'INVALID_REGION_FORMAT',
    
    // Selection and Validation
    'NO_VMS_SELECTED', 'INVALID_VM_IDS', 'NO_INSTANCES_SELECTED', 'INVALID_FIELDS',
    
    // Credits & Billing
    'INSUFFICIENT_CREDITS', 'PAYMENT_REQUIRED', 'CURRENCY_NOT_SET',
    'INVALID_CURRENCY', 'INSUFFICIENT_MINIMUM_BALANCE', 'contact_support',
    
    // Transaction Errors
    'TRANSACTION_NOT_FOUND', 'TRANSACTION_HISTORY_FAILED',
    'TRANSACTION_SUMMARY_FAILED', 'BILLING_DATA_FAILED', 'BALANCE_CALCULATION_FAILED',
    
    // Profile Errors
    'PROFILE_FETCH_FAILED', 'PROFILE_UPDATE_FAILED',
    'PROFILE_COMPLETENESS_CHECK_FAILED', 'COUNTRY_REQUIRED',
    'PUBLIC_IP_ALREADY_ATTACHED',
'NO_PUBLIC_IP_ATTACHED',
'PUBLIC_IP_OPERATION_FAILED',
'PUBLIC_IP_ERROR',
'DELETE_ERROR',
'VM_DETAILS_ERROR',
    // Payment Errors
    'MISSING_AMOUNT', 'INVALID_AMOUNT', 'BELOW_MINIMUM_AMOUNT',
    'PAYMENT_API_ERROR', 'VERIFICATION_FAILED', 'PAYMENT_NOT_CAPTURED',
    'PAYMENT_DETAILS_ERROR', 'DUPLICATE_ORDER', 'MISSING_PARAMETERS',
    'INVALID_ORDER_ID', 'ORDER_NOT_FOUND', 'PAYMENT_FETCH_ERROR', 'FETCH_ERROR',
    'MISSING_ORDER_ID', 'MISSING_USER_PROFILE', 'CURRENCY_ERROR',
    'PAYMENT_INTENT_FAILED', 'PAYMENT_CONFIRMATION_FAILED',
    
  // SSH Key Errors
    'SSH_KEY_NOT_FOUND', 'SSH_KEY_ALREADY_EXISTS', 'SSH_KEY_CREATE_FAILED',
    'SSH_KEY_DELETE_FAILED', 'SSH_KEY_IMPORT_FAILED', 'SSH_KEY_LIST_FAILED',
    'INVALID_SSH_KEY_FORMAT', 'INVALID_SSH_KEY_NAME', 'SSH_KEY_IMPORTED',
    'SSH_KEY_CREATED', 'SSH_KEY_DELETED', 'SSH_KEYS_RETRIEVED',
    'SSH_KEY_REQUIRED', 'ADMIN_PASSWORD_REQUIRED', 'SSH_KEY_LIMIT_EXCEEDED',
    'SSH_KEY_NAME_EXISTS', 'SSH_KEY_ALREADY_DELETED', 'SSH_KEY_FETCH_FAILED',
    'SSH_KEY_RETRIEVED',
    // Script Errors
    'SCRIPT_NOT_FOUND', 'SCRIPT_ACCESS_DENIED', 'INVALID_SCRIPT_NAME',
    'INVALID_SCRIPT_CONTENT', 'SCRIPT_NAME_EXISTS', 'SCRIPT_CREATE_FAILED',
    'SCRIPT_UPDATE_CREATE_FAILED', 'SCRIPT_DELETE_FAILED', 'SCRIPTS_DELETE_FAILED',
    'SCRIPT_UPDATE_FAILED', 'SCRIPT_GET_FAILED', 'SCRIPT_LIST_FAILED',
    'SCRIPT_UPDATED_PARTIAL',
    
    // Script & OS Volume Errors
    'SCRIPT_ID_INVALID_FORMAT', 'SCRIPT_ID_NOT_OWNED', 'OS_VOLUME_NOT_OWNED',
    'OS_VOLUME_VALIDATION_FAILED',
    
    // VM Deployment Errors
    'DEPLOYMENT_ERROR', 'FLAVOR_NOT_FOUND', 'INVALID_FLAVOR',
    'STOCK_UNAVAILABLE', 'GPU_NOT_AVAILABLE', 'VM_DEPLOY_FAILED',
    'CALLBACK_REGISTRATION_FAILED', 'OS_IMAGE_REQUIRED', 'CLOUD_INIT_INVALID',
    'INVALID_GPU_CONFIG', 'OUT_OF_STOCK',
    
    // Hibernation Errors
    'HIBERNATE_FAILED', 'HIBERNATE_ERROR', 'RESTORE_FAILED', 'RESTORE_ERROR',
    
    // Rate Limiting
    'RATE_LIMIT_EXCEEDED', 'RATE_LIMITED',
    
    // Stock Errors
    'STOCK_SERVICE_ERROR', 'STOCK_DATA_PROCESSING_ERROR', 'STOCK_API_ERROR',
    
    // Instance Errors
    'INVALID_INSTANCE_ID_FORMAT', 'INSTANCE_NOT_FOUND', 'INSTANCE_NOT_COMPATIBLE',
    'INSTANCES_NOT_ACTIVE', 'INSTANCE_NOT_ACTIVE',
    
    // AI Specific Errors
    'CONTEXT_LENGTH_EXCEEDED', 'TIMEOUT', 'AI_SERVICE_ERROR', 'MODELS_ERROR',
    'AI_USAGE_HISTORY_FAILED', 'AI_USAGE_SUMMARY_FAILED',
    
    // Network & Connection Errors
    'UNEXPECTED_BODY', 'CONNECTION_ERROR', 'REQUEST_TIMEOUT', 'JSON_PARSE_ERROR',
    'INVALID_IP_FORMAT',
    
    // Database Errors
    'DATABASE_RETRY_FAILED',
    
    // Generic Errors
    'NETWORK_ERROR', 'REQUEST_TOO_LARGE'
  ]);


  // Upstream errors - API/service errors
  const upstreamErrors = new Set([
    'API_CONNECTION_ERROR', 'SERVICE_UNAVAILABLE', 'INVALID_API_RESPONSE', 
    'SERVICE_CONFIG_ERROR', 'SERVICE_ERROR'
  ]);
  
  if (publicErrors.has(errorCode)) {
    return ErrorCategory.PUBLIC;
  } else if (upstreamErrors.has(errorCode)) {
    return ErrorCategory.UPSTREAM;
  } else {
    return ErrorCategory.PRIVATE;
  }
}

  // Process error responses from backend (FIXED for backend format)
  static processErrorResponse(error) {
    let errorInfo = {
      success: false,
      code: 'SERVER_ERROR',
      error: 'Something went wrong. Please try again.',
      status: 500,
      category: ErrorCategory.PRIVATE,
      userMessage: PRIVATE_ERROR_MESSAGE,
      actions: {}
    };

    if (!error) {
      return errorInfo;
    }

    // Handle different error response formats
    if (typeof error === 'object') {
      // FIXED: Handle backend error response format: {success: false, code: "CODE", error: "message"}
      if (error.success === false && error.code && error.error) {
        errorInfo.code = error.code;
        errorInfo.error = error.error;
        errorInfo.status = error.status || 400;
        
        // FIXED: Extract fieldErrors directly from response (not from details)
        if (error.fieldErrors) {
          errorInfo.fieldErrors = error.fieldErrors;
        }
      }
      // Response data format from axios
      else if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (responseData.success === false && responseData.code && responseData.error) {
          errorInfo.code = responseData.code;
          errorInfo.error = responseData.error;
          errorInfo.status = error.response.status || 400;
          
          // FIXED: Extract fieldErrors directly from response data
          if (responseData.fieldErrors) {
            errorInfo.fieldErrors = responseData.fieldErrors;
          }
        } else {
          // Handle HTTP errors without structured response
          errorInfo.status = error.response.status || 500;
          errorInfo.error = responseData.error || responseData.message || error.message || 'Request failed';
          errorInfo.code = this.mapHttpStatusToCode(errorInfo.status);
        }
      }
      // Standard error object
      else if (error.message) {
        errorInfo.error = error.message;
        errorInfo.code = error.code || this.mapHttpStatusToCode(error.status || 500);
        errorInfo.status = error.status || 500;
      }
      // HTTP response format
      else if (error.status) {
        errorInfo.status = error.status;
        errorInfo.code = this.mapHttpStatusToCode(error.status);
        errorInfo.error = error.statusText || `HTTP ${error.status} error`;
      }
    } else if (typeof error === 'string') {
      errorInfo.error = error;
      errorInfo.code = 'UNKNOWN_ERROR';
    }

    // Categorize error and determine user message
    errorInfo.category = this.categorizeError(errorInfo.code, errorInfo.status);
    errorInfo.userMessage = this.getUserMessage(errorInfo.code, errorInfo.category, errorInfo.error);
    
    // Determine automatic actions
    errorInfo.actions = this.getErrorActions(errorInfo.code, errorInfo);

    return errorInfo;
  }

  // Map HTTP status codes to error codes (FIXED: Added 413)
  static mapHttpStatusToCode(status) {
    const statusCodeMap = {
      400: 'VALIDATION_ERROR',
      401: 'AUTH_ERROR',
      403: 'ACCESS_DENIED',
      404: 'RESOURCE_NOT_FOUND',
      409: 'RESOURCE_ALREADY_EXISTS',
      413: 'REQUEST_TOO_LARGE',
      422: 'VALIDATION_ERROR',
      429: 'RATE_LIMIT_EXCEEDED',
      500: 'SERVER_ERROR',
      502: 'SERVICE_UNAVAILABLE',
      503: 'SERVICE_UNAVAILABLE',
      504: 'SERVICE_UNAVAILABLE'
    };
    
    return statusCodeMap[status] || 'SERVER_ERROR';
  }
static getUserMessage(code, category, originalMessage) {
  if (category === ErrorCategory.UPSTREAM) {
    return UPSTREAM_ERROR_MESSAGE;
  } else if (category === ErrorCategory.PRIVATE) {
    return PRIVATE_ERROR_MESSAGE;
  } else {
    // PUBLIC category - return original message, let DeployVM handle it
    return originalMessage;
  }
}
  // Determine automatic actions based on error code (FIXED: Use direct error properties)
  static getErrorActions(code, errorInfo = {}) {
    const actions = {};
    
    // Handle redirects from backend
    if (errorInfo.redirect_to) {
      actions.redirect = errorInfo.redirect_to;
      actions.delay = 2000; // Give user time to read message
    } else {
      // Handle specific error actions
      switch (code) {
        case 'SESSION_EXPIRED':
        case 'AUTH_ERROR':
        case 'TOKEN_BLACKLISTED':
          actions.redirect = '/login';
          break;
        case 'INCOMPLETE_PROFILE':
          actions.redirect = '/account';
          break;
        case 'INSUFFICIENT_CREDITS':
          actions.redirect = '/billing';
          break;
     
        default:
          // No specific action needed
          break;
      }
    }
    
    return actions;
  }

  // Create standardized error response
  static createErrorResponse(error, context = '') {
    const processedError = this.processErrorResponse(error);
    
    // Log the error appropriately based on category
    if (processedError.category === ErrorCategory.PUBLIC) {
      logError(`${context}: ${processedError.code}`, {
        code: processedError.code,
        message: processedError.userMessage,
        context
      });
    } else if (processedError.category === ErrorCategory.UPSTREAM) {
      logError(`${context}: Upstream service issue`, {
        code: processedError.code,
        context
      });
    } else {
      logError(`${context}: Internal error`, {
        code: processedError.code,
        originalError: processedError.error,
        context
      }, error);
    }
    
    return processedError;
  }
}

// Sentry error logging helper
const logError = (message, context = {}, error = null) => {
  
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'api' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'api' },
      extra: context 
    });
  }
};

// Auth token management helpers
const getAuthToken = () => {
  return localStorage.getItem('access_token') || '';
};

// Helper function to get CSRF token from cookie
const getCSRFToken = () => {
  const cookieValue = document.cookie
    .split('; ')
    .find(row => row.startsWith('csrftoken='))
    ?.split('=')[1];
  return cookieValue || '';
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'accept': 'application/json',
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 30000, // 30 second timeout
});

// Add request interceptor to include authorization token
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    if (config.method !== 'get') {
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.headers['X-CSRFToken'] = getCSRFToken();
    }
    
    return config;
  },
  (error) => {
    logError('Request interceptor error', { 
      url: error.config?.url,
      method: error.config?.method 
    }, error);
    return Promise.reject(error);
  }
);

/**
 * RESPONSE INTERCEPTOR - ERROR HANDLING PRIORITY
 * 
 * The error handling follows this precise order:
 * 
 * 1. Backend Structured Error Format Check (FIRST - checked once)
 *    - If backend returns {success: false, code: "...", error: "..."}
 *    - Return immediately without checking status codes
 * 
 * 2. HTTP 413 (Request Too Large)
 *    - Must be handled before backend format check was removed
 * 
 * 3. HTTP 429 (Rate Limit)
 *    - Must be handled with immediate response
 * 
 * 4. HTTP Status Code Fallbacks (400, 403, 404, 409, 422, 500, 502/503/504)
 *    - Only reached if backend didn't send structured error
 *    - Provide generic fallback errors
 * 
 * 5. Network Errors (no response object)
 *    - Handle network/connection issues
 * 
 * 6. ErrorProcessor
 *    - Catch-all for any other error format
 * 
 * 7. HTTP 401 (Authentication)
 *    - Handled last to trigger session expiration
 */

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // ============================================================================
    // STEP 1: Check for backend structured error format FIRST (before all else)
    // ============================================================================
    const responseData = error.response?.data;
    if (responseData?.success === false && responseData?.code && responseData?.error) {
      return Promise.reject(responseData);
    }
    
    const status = error.response ? error.response.status : null;

    // ============================================================================
    // STEP 2: Handle 413 errors (request too large)
    // ============================================================================
    if (status === 413) {
      return Promise.reject({
        success: false,
        code: 'REQUEST_TOO_LARGE',
        error: 'Request size exceeds maximum allowed limit',
        status: 413,
        category: ErrorCategory.PUBLIC
      });
    }

    // ============================================================================
    // STEP 3: Handle 429 errors (rate limiting)
    // ============================================================================
    if (status === 429) {
      return Promise.reject({
        success: false,
        code: 'RATE_LIMIT_EXCEEDED',
        error: 'Too many requests. Please try again after 60 seconds',
        status: 429,
        category: ErrorCategory.PUBLIC
      });
    }
    
    // ============================================================================
    // STEP 4: HTTP Status Code Fallbacks
    // ============================================================================
    
    if (status === 400) {
      return Promise.reject({
        success: false,
        code: 'VALIDATION_ERROR',
        error: 'Please check your input and try again',
        status: 400,
        category: ErrorCategory.PUBLIC
      });
    }
    
    if (status === 403) {
      return Promise.reject({
        success: false,
        code: 'ACCESS_DENIED', 
        error: 'You do not have permission to perform this action',
        status: 403,
        category: ErrorCategory.PUBLIC
      });
    }
    
    if (status === 404) {
      return Promise.reject({
        success: false,
        code: 'RESOURCE_NOT_FOUND',
        error: 'The requested resource was not found',
        status: 404,
        category: ErrorCategory.PUBLIC
      });
    }
    
    if (status === 409) {
      return Promise.reject({
        success: false,
        code: 'RESOURCE_ALREADY_EXISTS',
        error: 'A resource with this name already exists',
        status: 409,
        category: ErrorCategory.PUBLIC
      });
    }
    
    if (status === 422) {
      return Promise.reject({
        success: false,
        code: 'VALIDATION_ERROR',
        error: 'Invalid data provided. Please check your input',
        status: 422,
        category: ErrorCategory.PUBLIC
      });
    }
    
    if (status === 500) {
      return Promise.reject({
        success: false,
        code: 'SERVER_ERROR',
        error: 'An internal error occurred. Please try again later',
        status: 500,
        category: ErrorCategory.PUBLIC
      });
    }

    if (status === 502 || status === 503 || status === 504) {
      return Promise.reject({
        success: false,
        code: 'SERVICE_UNAVAILABLE',
        error: 'Service is temporarily unavailable. Please try again later',
        status: status,
        category: ErrorCategory.PUBLIC
      });
    }
    
    // ============================================================================
    // STEP 5: Handle network errors (no response object)
    // ============================================================================
    if (!error.response) {
      let networkMessage = 'Network error. Please check your connection.';
      let networkCode = 'NETWORK_ERROR';
      
      // Position 1: Browser knows it's offline
      if (!navigator.onLine) {
        networkMessage = "You're offline. Check your connection.";
        networkCode = 'OFFLINE';
      }
      // Position 2: Request timeout
      else if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        networkMessage = 'Request timed out. Please try again.';
        networkCode = 'TIMEOUT';
      }
      // Position 3: Network error but browser thinks it's online
      else if (error.code === 'ERR_NETWORK') {
  networkMessage = 'Network connection failed. Please check your internet connection and try again.';
        networkCode = 'SERVER_UNREACHABLE';
      }
      // Position 4: Request sent but no response received
      else if (error.request && !error.response) {
        networkMessage = 'No response from server. Please try again.';
        networkCode = 'NO_RESPONSE';
      }
      
      return Promise.reject({
        success: false,
        code: networkCode,
        error: networkMessage,
        category: ErrorCategory.PUBLIC
      });
    }
    
    // ============================================================================
    // STEP 6: Process other errors through ErrorProcessor
    // ============================================================================
    const processedError = ErrorProcessor.processErrorResponse(error);
    
    // ============================================================================
    // STEP 7: Handle 401 errors (unauthorized)
    // ============================================================================
    if (status === 401) {
      if (window.location.pathname === '/deploy') {
        return Promise.reject(processedError);
      }
      
      window.dispatchEvent(new Event('auth:sessionExpired'));
      
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?session=expired';
      }
    }
    
    return Promise.reject(processedError);
  }
);
export const VALID_GPUS = {
  'B300 SXM6 262GB': {
    'max_count': 8,
    'price': {'USD': 8.99, 'EUR': 9.65, 'INR': 899},
  },
  'B200 SXM6 180GB': {
    'max_count': 8,
    'price': {'USD': 6.89, 'EUR': 7.39, 'INR': 689},
  },
  'H200 SXM5 141GB': {
    'max_count': 8,
    'price': {'USD': 4.45, 'EUR': 4.79, 'INR': 445},
  },
  'H100-SXM5-80GB': {
    'max_count': 8,
    'price': {'USD': 3.59, 'EUR': 3.89, 'INR': 359},
  },
  'H100-PCIe-NVLink-80GB': {
    'max_count': 8,
    'price': {'USD': 3.19, 'EUR': 3.45, 'INR': 319},
  },
  'H100-PCIe-80GB': {
    'max_count': 8,
    'price': {'USD': 2.89, 'EUR': 3.09, 'INR': 289},
  },
  'A100-PCIe-80GB': {
    'max_count': 8,
    'price': {'USD': 1.49, 'EUR': 1.59, 'INR': 149},
  },
  'L40': {
    'max_count': 4,
    'price': {'USD': 1.19, 'EUR': 1.29, 'INR': 119},
  },
  'RTX-A6000': {
    'max_count': 4,
    'price': {'USD': 0.59, 'EUR': 0.65, 'INR': 59},
  }
};

// Input validation helpers
const validatePort = (port) => {
  const portNum = parseInt(port, 10);
  return !isNaN(portNum) && portNum >= 1 && portNum <= 65535;
};

const validateCIDR = (cidr) => {
  if (!cidr) return true; // Empty allowed
  const cidrRegex = /^([0-9]{1,3}\.){3}[0-9]{1,3}\/([0-9]|[1-2][0-9]|3[0-2])$/;
  return cidrRegex.test(cidr);
};

// Enhanced API service methods with systematic error handling (REMOVED RATE LIMITING)
export const api = {
// ==================== GPU STOCKS API ====================
// Get real-time GPU stock availability (Public - No Auth Required)
async getBarrackStocks() {
  try {
    const response = await apiClient.get('/api/barrack-stocks/');
    return response.data;
  } catch (error) {
    // Check for processed backend error format
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch GPU stock data');
  }
},

// ==================== GPU SPECIFICATIONS API ====================
// Get GPU specifications with pricing (Public - No Auth Required)
async getBarrackSpecs() {
  try {
    const response = await apiClient.get('/api/barrack-specs/');
    return response.data;
  } catch (error) {
    // Check for processed backend error format
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch GPU specifications');
  }
},

async getImages(region = null, gpuType = null) {
    try {
      let url = '/api/os-images/';
      const params = [];
      
      if (region) {
        params.push(`region=${encodeURIComponent(region)}`);
      }
      
      if (gpuType) {
        params.push(`gpu_type=${encodeURIComponent(gpuType)}`);
      }
      
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch images');
    }
  },
  // Verify registration with OTP
  verifyRegistration: async (email, otp) => {
    try {
      const response = await apiClient.post('/api/auth/oauth/verify-registration', {
        email,
        code: otp
      });
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to verify registration');
    }
  },

  // Register user (for both initial signup and resend)
  registerUser: async (email, password) => {
    try {
      if (window.debugTokenManager) {
        window.debugTokenManager.stopProactiveRefresh();
      }
      const response = await apiClient.post('/api/auth/oauth/register', {
        email,
        password
      });
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to register user');
    }
  },

  // User authentication and profile

  // Get credit balance
  getCreditBalance: async () => {
    try {
      const response = await apiClient.get('/api/me/balance/');
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch credit balance');
    }
  },
  
  // Update GSTIN or Tax ID
  updateTaxInfo: async (taxData) => {
    try {
      const response = await apiClient.post('/api/user/gstin/', taxData);
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to update tax information');
    }
  },

  logoutUser: async () => {
    try {
      tokenManager.stopProactiveRefresh();
      const response = await apiClient.post('/api/auth/oauth/logout', {});
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to logout');
    }
  },

  // AI Chat API Functions - Fixed and Complete
  // Get available AI models with pricing

  // Send AI chat completion (non-streaming)
 


  // Get VMs with pagination and filters
  getVMs: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value);
        }
      });
      
      const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await apiClient.get(`/api/barrack/${query}`);
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch VMs');
    }
  },
  
  // Delete a VM
  deleteVM: async (vmId) => {
    try {
      const response = await apiClient.delete(`/api/barrack/${vmId}/delete/`);
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to delete VM');
    }
  },
  
  // SSH Key management APIs
 async getSSHKeys() {
  try {
    const response = await apiClient.get('/api/ssh-keys/');
    return response.data;
  } catch (error) {
    if (error.response?.data?.success === false) {
      throw error.response.data;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch SSH keys');
  }
},

  async getSSHKeysPage() {
    try {
      const response = await apiClient.get('/api/ssh-keys-page');
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch SSH keys for page');
    }
  },

async createSSHKey(name) {
  try {
    const response = await apiClient.post('/api/ssh-keys/', { name });
    return response.data;
  } catch (error) {
    if (error.response?.data?.success === false) {
      throw error.response.data;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to create SSH key');
  }
},

async importSSHKey(name, publicKey) {
  try {
    const response = await apiClient.post('/api/ssh-keys/', { 
      name, 
      public_key: publicKey 
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.success === false) {
      throw error.response.data;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to import SSH key');
  }
},

async deleteSSHKey(keyId) {
  try {
    const response = await apiClient.delete(`/api/ssh-keys/${keyId}/`);
    return response.data;
  } catch (error) {
    if (error.response?.data?.success === false) {
      throw error.response.data;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to delete SSH key');
  }
},

  // ==================== SCRIPT MANAGEMENT APIs ====================

// Get all startup scripts (NO CHANGE)
async getScripts() {
  try {
    const response = await apiClient.get('/api/scripts/');
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch scripts');
  }
},

// Get single script by ID (NO CHANGE)
async getScript(scriptId) {
  try {
    if (!scriptId) {
      throw new Error('Script ID is required');
    }
    
    const response = await apiClient.get(`/api/scripts/${scriptId}/`);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch script');
  }
},
// ==================== CONTACT / LEAD CAPTURE ====================

async submitContact(email, message = '') {
  try {
    const response = await apiClient.post('/v1/contact/', {
      email,
      message: message || '',
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.success === false) {
      throw error.response.data;
    }

    throw ErrorProcessor.createErrorResponse(error, 'Failed to submit contact');
  }
},// ==================== COMPARISON / EMAIL ME THIS ====================

async submitComparison(email, provider, gpu, gpuCount, hours, providerCost, barrackCost, savingsAmount, savingsPct) {
  try {
    const response = await apiClient.post('/v1/contact/comparison/', {
      email,
      provider,
      gpu_model: gpu,
      gpu_count: gpuCount,
      hours: parseFloat(hours),
      provider_cost: providerCost,
      barrack_cost: barrackCost,
      savings_amount: savingsAmount,
      savings_pct: savingsPct,
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.success === false) {
      throw error.response.data;
    }
    throw ErrorProcessor.createErrorResponse(error, 'Failed to submit comparison');
  }
},

// Create new startup script (URL CHANGED - removed /create/)
async createScript(scriptData) {
  try {
    if (!scriptData || typeof scriptData !== 'object') {
      throw new Error('Invalid script data format');
    }
    
    if (!scriptData.name || !scriptData.script) {
      throw new Error('Script name and content are required');
    }
    
    if (scriptData.name.length > 30) {
      throw new Error('Script name must be 30 characters or less');
    }
    const byteSize = new Blob([scriptData.script]).size;

    if (scriptData.script.length > 16384) {
      throw new Error('Script content must be 16KB or less');
    }
    
    const response = await apiClient.post('/api/scripts/', scriptData);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to create script');
  }
},

// Delete single startup script (URL CHANGED - removed /delete/)
async deleteScript(scriptId) {
  try {
    if (!scriptId) {
      throw new Error('Script ID is required');
    }
    
    const response = await apiClient.delete(`/api/scripts/${scriptId}/`);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to delete script');
  }
},

// Delete multiple startup scripts (NO CHANGE - special bulk operation)
async deleteMultipleScripts(scriptIds) {
  try {
    if (!scriptIds || !Array.isArray(scriptIds) || scriptIds.length === 0) {
      throw new Error('Script IDs array is required');
    }
    
    if (scriptIds.length > 50) {
      throw new Error('Cannot delete more than 50 scripts at once');
    }
    
    for (const scriptId of scriptIds) {
      if (!scriptId || typeof scriptId !== 'string') {
        throw new Error('All script IDs must be valid strings');
      }
    }
    
    const response = await apiClient.delete('/api/scripts/bulk-delete/', {
      data: { script_ids: scriptIds }
    });
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to delete scripts');
  }
},

  // Get firewall rules
  async getFirewallRules() {
    try {
      const response = await apiClient.get('/api/security-rules');
      return response.data.rules || [];
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch firewall rules');
    }
  },

  // Create firewall rule
  async createFirewallRule(rule) {
    try {
      if (!rule || typeof rule !== 'object') {
        throw new Error('Invalid rule format');
      }
      
      if (!rule.name || !rule.protocol) {
        throw new Error('Missing required rule fields');
      }
      
      if (rule.protocol !== 'icmp') {
        if (!validatePort(rule.port_range_min)) {
          throw new Error('Invalid port range minimum');
        }
        
        if (rule.port_range_max && !validatePort(rule.port_range_max)) {
          throw new Error('Invalid port range maximum');
        }
      }
      
      if (rule.remote_ip_prefix && !validateCIDR(rule.remote_ip_prefix)) {
        throw new Error('Invalid CIDR format');
      }
      
      const response = await apiClient.post('/security-rules', rule);
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to create firewall rule');
    }
  },
  
  // Create VM
  async createVM(config) {
    try {
      if (!config || typeof config !== 'object') {
        throw new Error('Invalid VM configuration');
      }
      
      if (!config.image_name || !config.flavor_name) {
        throw new Error('Missing required VM configuration fields');
      }
      
      if (!config.ssh_key && !config.admin_password) {
        throw new Error('Either SSH key or admin password is required');
      }
      
      if (config.user_data && config.user_data.length > 16384) {
        config.user_data = config.user_data.substring(0, 16384);
      }
      
      const response = await apiClient.post('/api/virtual-machines', config);
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to create VM');
    }
  },
  
  // Snapshot management APIs
  async getSnapshots() {
    try {
      const response = await apiClient.get('/api/snapshots/');
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch snapshots');
    }
  },
  
  async getSnapshotDetails(snapshotId) {
    try {
      const response = await apiClient.get(`/api/snapshots/${snapshotId}/`);
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch snapshot details');
    }
  },
  
async createSnapshot(vmId, snapshotData) {
  try {
    const response = await apiClient.post(`/api/instances/${vmId}/snapshots/create/`, snapshotData);
    return response.data;
  } catch (error) {
    // FIXED: Check for processed backend error format
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to create snapshot');
  }
},
  async deleteSnapshot(snapshotId) {
    try {
      const response = await apiClient.delete(`/api/snapshots/${snapshotId}/delete/`);
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to delete snapshot');
    }
  },
  
  async restoreSnapshot(snapshotId, restoreData) {
    try {
      const response = await apiClient.post(`/api/snapshots/${snapshotId}/restore/`, restoreData);
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to restore snapshot');
    }
  },
  
async listVmsForSnapshot() {
  try {
    const response = await apiClient.get('/api/snapshots/instances/list/');
    return response.data;
  } catch (error) {
    // FIXED: Check for processed backend error format
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch VMs for snapshot');
  }
},
  
  // Get custom images (type=Snapshot)
  getCustomImages: async () => {
    try {
      const response = await apiClient.get('/api/custom-images/');
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch custom images');
    }
  },
  
  // Get image details
  getImageDetails: async (imageId) => {
    try {
      const response = await apiClient.get(`/api/images/${imageId}/`);
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch image details');
    }
  },
  
  // Toggle VM public IP
  toggleVMPublicIP: async (vmId, action) => {
    try {
      const response = await apiClient.post(`/api/barrack/${vmId}/toggle-public-ip/`, { action });
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, `Failed to ${action} public IP`);
    }
  },
  
  // Start VM
  startVM: async (vmId) => {
    try {
      const response = await apiClient.post(`/api/barrack/${vmId}/start/`, {});
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to start VM');
    }
  },
  
  // Stop VM
  stopVM: async (vmId) => {
    try {
      const response = await apiClient.post(`/api/barrack/${vmId}/stop/`, {});
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to stop VM');
    }
  },
  
  // Reboot VM
  rebootVM: async (vmId) => {
    try {
      const response = await apiClient.post(`/api/barrack/${vmId}/reboot/`, {});
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to reboot VM');
    }
  },
  
  // Create image from snapshot
  createImageFromSnapshot: async (snapshotId, imageData) => {
    try {
      const response = await apiClient.post(`/api/snapshots/${snapshotId}/image/`, imageData);
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to create image from snapshot');
    }
  },
  
  // Hibernate VM
  hibernateVM: async (vmId) => {
    try {
      const response = await apiClient.post(`/api/barrack/${vmId}/hibernate/`, {});
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to hibernate VM');
    }
  },
  
  // Restore hibernated VM
  restoreHibernatedVM: async (vmId) => {
    try {
      const response = await apiClient.post(`/api/barrack/${vmId}/restore/`, {});
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to restore hibernated VM');
    }
  },
  
  // Delete image
  deleteImage: async (imageId) => {
    try {
      const response = await apiClient.delete(`/api/images/${imageId}/delete/`);
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to delete image');
    }
  },
  
// ==================== USER PROFILE FUNCTIONS ====================

// Get user profile (URL CHANGED: /api/user/profile/ → /api/me/)
getUserProfile: async () => {
  try {
    const response = await apiClient.get('/api/me/');
    return response.data;
  } catch (error) {
    // FIXED: Check for processed backend error format
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch user profile');
  }
},

// Update user profile (URL CHANGED: /api/user/profile/update/ → /api/me/ AND METHOD CHANGED: POST → PUT)
updateUserProfile: async (profileData) => {
  try {
    if (profileData.country && profileData.country.length !== 2) {
      throw new Error('Country must be a valid 2-letter ISO country code');
    }
    
    const hasAddressData = profileData.address_line1 || 
                        profileData.address_city || 
                        profileData.address_state || 
                        profileData.address_postal_code ||
                        profileData.country;
    
    if (hasAddressData) {
      if (!profileData.address_line1) {
        throw new Error('Address line is required');
      }
      if (!profileData.address_postal_code) {
        throw new Error('Postal code is required');
      }
      if (!profileData.country) {
        throw new Error('Country is required');
      }
    }
    
    const response = await apiClient.put('/api/me/', profileData);
    return response.data;
  } catch (error) {
    // FIXED: Check for processed backend error format
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to update user profile');
  }
},

// Check profile complete (URL CHANGED: /api/user/profile/complete/ → /api/me/complete/)
checkProfileComplete: async () => {
  try {
    const response = await apiClient.get('/api/me/complete/');
    return response.data;
  } catch (error) {
    // FIXED: Check for processed backend error format
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to check profile completeness');
  }
},

  // Remove security rule from VM
removeVmSecurityRule: async (vmId, ruleId) => {
  try {
    const response = await apiClient.delete(`/api/instances/${vmId}/security-rules/${ruleId}/`);
    return response.data;
  } catch (error) {
    // FIXED: Check for processed backend error format
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to remove security rule from VM');
  }
},
  
  // Get firewalls attached to a VM
// Get firewalls attached to a VM
getVmAttachedFirewalls: async (vmId) => {
  try {
    const response = await apiClient.get(`/api/instances/${vmId}/attached-firewalls/`);
    return response.data;
  } catch (error) {
    // FIXED: Check for processed backend error format
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch firewalls attached to VM');
  }
},
// Add security rule to VM
addVmSecurityRule: async (vmId, ruleData) => {
  try {
    const response = await apiClient.post(`/api/instances/${vmId}/security-rules/`, ruleData);
    return response.data;
  } catch (error) {
    // FIXED: Check for processed backend error format
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to add security rule to VM');
  }
},

  getBarrackVMs: async () => {
    try {
      const response = await apiClient.get('/api/barrack/');
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch VMs');
    }
  },

  getBarrackVMDetails: async (vmId) => {
    try {
      const response = await apiClient.get(`/api/barrack/details/${vmId}/`);
      return response.data;
    } catch (error) {
      // FIXED: Check for processed backend error format
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch VM details');
    }
  },

deleteBarrackVM: async (vmId) => {
    try {
      const response = await apiClient.delete(`/api/barrack/${vmId}/`);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to delete VM');
    }
  },

  // Create firewall
  async createFirewall(firewallData) {
    try {
      const response = await apiClient.post('/api/firewalls/create/', firewallData);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to create firewall');
    }
  },

  // Delete firewall
  async deleteFirewall(firewallId) {
    try {
      const response = await apiClient.delete(`/api/firewalls/${firewallId}/delete/`);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to delete firewall');
    }
  },

  // Add firewall rule
  async addFirewallRule(firewallId, ruleData) {
    try {
      const response = await apiClient.post(`/api/firewalls/${firewallId}/rules/add/`, ruleData);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to add firewall rule');
    }
  },

  // Remove firewall rule
  async removeFirewallRule(firewallId, ruleId) {
    try {
      const response = await apiClient.delete(`/api/firewalls/${firewallId}/rules/${ruleId}/remove/`);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to remove firewall rule');
    }
  },

  // List VMs for firewall
  async listVmsForFirewall() {
    try {
      const response = await apiClient.get('/api/firewalls/instances/list/');
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch VMs for firewall');
    }
  },

  // Attach firewall to VMs
  async attachFirewallToVMs(firewallId, vmIds) {
    try {
      const response = await apiClient.post(`/api/firewalls/${firewallId}/attach/`, {
        vm_ids: vmIds
      });
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to attach firewall to VMs');
    }
  },

  // Get firewall protocols
  getFirewallProtocols: async () => {
    try {
      const response = await apiClient.get('/api/firewalls/protocols/');
      return response.data;
    } catch (error) {
      // Return default protocols if API fails
      return { 
        success: true, 
        protocols: [
          { id: 'tcp', name: 'TCP' },
          { id: 'udp', name: 'UDP' },
          { id: 'icmp', name: 'ICMP' }
        ]
      };
    }
  },

  createPaymentWithProfile: async (amount, currency = null, options = {}) => {
    try {
      const response = await apiClient.post('/api/razorpay/create-order/', {
        amount: amount,
        currency: currency,
        includeGST: currency === 'INR'
      });
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to create payment order');
    }
  },

  // Initiate social login (Google, etc.)
  async initiateSocialLogin(provider, intent = 'login') {
    try {
      const response = await apiClient.get(`/api/auth/oauth/${provider}/init`, {
        headers: {
          'Accept': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to initialize social login');
    }
  },

  // Handle social signup callback
  async handleSocialSignupCallback(code, state) {
    try {
      const response = await apiClient.get(`/api/auth/oauth/google/callback?code=${code}&state=${state}`, {
        headers: {
          'Accept': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to complete OAuth authentication');
    }
  },

  // Handle social login
  async handleSocialLogin(email, provider) {
    try {
      const response = await apiClient.post('/api/auth/oauth/social-login', {
        email,
        provider
      });
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to complete social login');
    }
  },

  // Check if user exists for login
  async checkUserExists(email, provider) {
    try {
      const response = await apiClient.get(
        `/api/auth/oauth/check_user_exists?email=${encodeURIComponent(email)}&provider=${encodeURIComponent(provider)}`
      );
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to check user existence');
    }
  },

  // Direct email/password login
  async directLogin(email, password) {
    try {
      const response = await apiClient.post('/api/auth/oauth/login', {
        email,
        password,
        provider: 'email'
      });
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Login failed');
    }
  },

  // Check current session
  async checkAuthSession() {
    try {
      const response = await apiClient.get('/api/auth/oauth/session', {
        headers: {
          'Accept': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      return { 
        authenticated: false,
        error: 'Failed to check session'
      };
    }
  },

  // Get barrack ID for user
  async getBarrackId(email, isLoginFlow = false) {
    try {
      const response = await apiClient.post('/api/auth/get-barrack-id/', {
        email,
        isLoginFlow
      });
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to get barrack ID');
    }
  },

// ==================== API KEYS FUNCTIONS ====================

// List API Keys (no change)
async listAPIKeys() {
  try {
    const response = await apiClient.get('/api/api-keys/');
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch API keys');
  }
},

// Create API Key (URL CHANGED - removed /create/)
async createAPIKey(keyData) {
  try {
    const response = await apiClient.post('/api/api-keys/', keyData);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to create API key');
  }
},

// Delete API Key (URL CHANGED - removed /delete/)
async deleteAPIKey(keyId) {
  try {
    const response = await apiClient.delete(`/api/api-keys/${keyId}/`);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to delete API key');
  }
},


  
  // Refresh token
  async refreshAuthToken() {
    try {
      const response = await apiClient.post('/api/auth/oauth/refresh-token', {
        t: Date.now()
      });
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to refresh token');
    }
  },

  // Verify Razorpay payment
  verifyRazorpayPayment: async (paymentData) => {
    try {
      const response = await apiClient.post('/api/razorpay/verify-payment/', paymentData);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to verify payment');
    }
  },

  // Get Razorpay payment status
  getRazorpayPaymentStatus: async (orderId) => {
    try {
      const response = await apiClient.get(`/api/razorpay/payment-status/${orderId}/`);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to get payment status');
    }
  },

  // Get Razorpay payment methods
  getRazorpayPaymentMethods: async () => {
    try {
      const response = await apiClient.get('/api/razorpay/payment-methods/');
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to get payment methods');
    }
  },

  getRazorpayTransactions: async (filters = {}) => {
    try {
      let url = '/api/razorpay/transactions/';
      const params = new URLSearchParams();
      
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to get transaction history');
    }
  },

  async getFirewalls() {
    try {
      const response = await apiClient.get('/api/firewalls/');
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch firewalls');
    }
  },

  // Get volume types
getVolumeTypes: async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.region) queryParams.append('region', filters.region);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await apiClient.get(`/api/volumes/types/${query}`);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch volume types');
  }
},
  // Volume attachment and detachment
attachVolumes: async (vmId, volumeIds) => {
  try {
    const response = await apiClient.post(`/api/instances/${vmId}/attach-volumes/`, { 
      volume_ids: Array.isArray(volumeIds) ? volumeIds : [volumeIds] 
    });
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to attach volumes');
  }
},
detachVolumes: async (vmId, volumeIds) => {
  try {
    const response = await apiClient.post(`/api/instances/${vmId}/detach-volumes/`, { 
      volume_ids: Array.isArray(volumeIds) ? volumeIds : [volumeIds] 
    });
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to detach volumes');
  }
},  // Get volumes with next volume name

  // Get Volumes
getVolumes: async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.region) queryParams.append('region', filters.region);
    if (filters.bootable !== undefined) queryParams.append('bootable', filters.bootable);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.exclude_os_volumes !== undefined) queryParams.append('exclude_os_volumes', filters.exclude_os_volumes);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await apiClient.get(`/api/volumes/browse/${query}`);
    
    if (response.data.success && Array.isArray(response.data.volumes)) {
      return response.data;
    } else if (Array.isArray(response.data)) {
      return { success: true, volumes: response.data };
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch volumes');
  }
},

getVolumesWithNextName: async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.region) queryParams.append('region', filters.region);
    if (filters.tier) queryParams.append('tier', filters.tier);
    if (filters.bootable !== undefined) queryParams.append('bootable', filters.bootable);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await apiClient.get(`/api/volumes/${query}`);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch volumes');
  }
},

getVolumeDetails: async (volumeId) => {
  try {
    if (!volumeId) {
      throw new Error('Volume ID is required');
    }
    
    const response = await apiClient.get(`/api/volumes/${volumeId}/`);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch volume details');
  }
},
  // Create volume
createVolume: async (volumeData) => {
  try {
    if (!volumeData || typeof volumeData !== 'object') {
      throw new Error('Invalid volume configuration');
    }
    
    if (!volumeData.name || !volumeData.size) {
      throw new Error('Missing required volume fields');
    }
    
    const response = await apiClient.post('/api/volumes/', volumeData);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to create volume');
  }
},

  // Resize volume
resizeVolume: async (volumeId, newSize) => {
  try {
    const response = await apiClient.post(`/api/volumes/${volumeId}/resize/`, {
      new_size: newSize
    });
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to resize volume');
  }
},

  // Clone volume
cloneVolume: async (volumeId, cloneName, targetRegion, volumeType) => {
  try {
    const response = await apiClient.post(`/api/volumes/${volumeId}/clone/`, {
      clone_name: cloneName,
      target_region: targetRegion,
      volume_type: volumeType
    });
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to clone volume');
  }
},
deleteVolume: async (volumeId) => {
  try {
    if (!volumeId) {
      throw new Error('Volume ID is required');
    }
    
    const response = await apiClient.delete(`/api/volumes/${volumeId}/`);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to delete volume');
  }
},
getVolumeHourlyBilling: async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await apiClient.get(`/api/volumes/billing/${query}`);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch volume hourly billing');
  }
},
  // Get user auth profile (different from existing getUserProfile)
  getUserAuthProfile: async () => {
    try {
      const response = await apiClient.get('/api/auth/user-profile/');
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch user profile');
    }
  },

  // Get VMs list for event logs
  getVMsList: async () => {
    try {
      const response = await apiClient.get('/api/vms/list/');
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch VMs list');
    }
  },

  // Get events for a specific VM
  getVMEvents: async (vmId) => {
    try {
      if (!vmId) {
        throw new Error('VM ID is required');
      }
      
      const response = await apiClient.get(`/api/vms/${vmId}/events/`);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch VM events');
    }
  },
deployServer: async (vmConfig) => {
  try {
    if (!vmConfig || typeof vmConfig !== 'object') {
      throw new Error('Invalid VM configuration');
    }
    
    // Only validate fields that are actually present
    // Backend will do proper tier-specific validation
    if (!vmConfig.gpu_model || !vmConfig.gpu_count || !vmConfig.region) {
      throw new Error('Missing required fields: gpu_model, gpu_count, and region');
    }
    
    // Truncate cloud-init if too large
    if (vmConfig.user_data && vmConfig.user_data.length > 16384) {
      vmConfig.user_data = vmConfig.user_data.substring(0, 16384);
    }

    const response = await apiClient.post('/api/deploy-server/', vmConfig);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to deploy VM');
  }
},

  // Transaction History
  getTransactionHistory: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await apiClient.get(`/api/transaction-history/${query}`);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch transaction history');
    }
  },


  // Aggregated Deployment Billing
getAggregatedDeploymentBilling: async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await apiClient.get(`/api/instances/billing/${query}`);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch aggregated deployment billing');
  }
},
  // Deposits
  getDeposits: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await apiClient.get(`/api/credits/${query}`);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch deposits');
    }
  },

  // Deposits Summary
  getDepositsSummary: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await apiClient.get(`/api/credits/summary/${query}`);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch deposits summary');
    }
  },

  // Get VM Status (for deployment monitoring)
 

  getBatchSnapshotImageRelationships: async (snapshotIds) => {
    try {
      const response = await apiClient.post('/api/snapshots/image-relationships/', {
        snapshot_ids: snapshotIds
      });
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch batch snapshot image relationships');
    }
  },

  // Get firewall details
  async getFirewallDetails(firewallId) {
    try {
      const response = await apiClient.get(`/api/firewalls/${firewallId}/`);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch firewall details');
    }
  },

 
  // Transaction Summary
  getTransactionSummary: async (barrackId) => {
    try {
      const response = await apiClient.get(`/api/transactions/summary/?barrack_id=${barrackId}`);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch transaction summary');
    }
  },

  // Volume Hourly Billing

  // Snapshot Hourly Billing
getSnapshotHourlyBilling: async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await apiClient.get(`/api/snapshots/billing/${query}`);
    return response.data;
  } catch (error) {
    if (error.success === false && error.code && error.error) {
      throw error;
    }
    
    throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch snapshot hourly billing');
  }
},


  getSnapshotImageRelationship: async (snapshotId) => {
    try {
      const response = await apiClient.get(`/api/snapshots/${snapshotId}/image-relationship/`);
      return response.data;
    } catch (error) {
      if (error.success === false && error.code && error.error) {
        throw error;
      }
      
      throw ErrorProcessor.createErrorResponse(error, 'Failed to fetch snapshot image relationship');
    }
  },
};
  
  
// WebSocket Service Class for real-time updates
class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.handlers = {
      stocks: [],
      flavors: [],
      all: []
    };
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.connectWithBackoff = true;
    this.connectionTimeout = 10000;
    this.connectionTimeoutId = null;
    this.reconnectTimeout = null;
    this.pingInterval = null;
    this.lastMessageTime = Date.now();
    this.connectionStartTime = 0;
  }

  connect() {
    try {
      this.connectionStartTime = Date.now();
      this.socket = new WebSocket(WS_URL);
      this.connectionTimeoutId = setTimeout(() => {
        if (!this.isConnected) {
          logError('WebSocket connection timeout', { 
            url: WS_URL,
            connectionTime: Date.now() - this.connectionStartTime 
          });
          this.disconnect();
          this.attemptReconnect();
        }
      }, this.connectionTimeout);

      this.socket.onopen = this.handleOpen.bind(this);
      this.socket.onclose = this.handleClose.bind(this);
      this.socket.onerror = this.handleError.bind(this);
      this.socket.onmessage = this.handleMessage.bind(this);
    } catch (error) {
      logError('WebSocket connection error', { url: WS_URL }, error);
      this.attemptReconnect();
    }

    return this;
  }

  handleOpen() {
    this.isConnected = true;
    this.reconnectAttempts = 0;
    clearTimeout(this.connectionTimeoutId);

    this.subscribe('all');
    this.startPingInterval();
    
    window.dispatchEvent(new CustomEvent('websocket:connected', {
      detail: {
        timestamp: Date.now(),
        connectionTime: Date.now() - this.connectionStartTime
      }
    }));
  }

  handleClose(event) {
    clearTimeout(this.connectionTimeoutId);
    this.isConnected = false;
    this.clearPingInterval();

    if (event.wasClean) {
    } else {
      logError('WebSocket connection dropped unexpectedly', { 
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean 
      });
      this.attemptReconnect();
    }
    
    window.dispatchEvent(new CustomEvent('websocket:disconnected', {
      detail: {
        timestamp: Date.now(),
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      }
    }));
  }

  handleError(error) {
    logError('WebSocket error', { 
      url: WS_URL,
      errorType: error.type 
    }, error);
    this.isConnected = false;
    
    window.dispatchEvent(new CustomEvent('websocket:error', {
      detail: {
        timestamp: Date.now(),
        errorType: error.type
      }
    }));
  }

  handleMessage(event) {
    this.lastMessageTime = Date.now();
    
    try {
      if (!event.data || typeof event.data !== 'string') {
        throw new Error('Invalid message format');
      }
      
      if (event.data.length > 1048576) { // 1MB
        throw new Error('Message size exceeds limit');
      }
      
      const message = JSON.parse(event.data);
      
      if (!message || !message.type) {
        throw new Error('Missing message type');
      }
      
      const { type, data } = message;
      
      if (type === 'stocks' && Array.isArray(this.handlers.stocks)) {
        this.handlers.stocks.forEach((handler) => {
          try {
            handler(data);
          } catch (handlerError) {
            logError('Error in stocks handler', { type, data }, handlerError);
          }
        });
        this.handlers.all.forEach((handler) => {
          try {
            handler(message);
          } catch (handlerError) {
            logError('Error in all handler', { type, data }, handlerError);
          }
        });
      } else if (type === 'flavors' && Array.isArray(this.handlers.flavors)) {
        this.handlers.flavors.forEach((handler) => {
          try {
            handler(data);
          } catch (handlerError) {
            logError('Error in flavors handler', { type, data }, handlerError);
          }
        });
        this.handlers.all.forEach((handler) => {
          try {
            handler(message);
          } catch (handlerError) {
            logError('Error in all handler', { type, data }, handlerError);
          }
        });
      } else if (type === 'pong') {
        // Handle pong response
      } else if (type === 'error') {
        logError('Server reported WebSocket error', {
          serverError: data,
          messageType: type
        });
      }
 } catch (error) {
  logError('Error parsing WebSocket message', {
    messageLength: event.data?.length,
    messageType: typeof event.data,
    parseError: error.message
  }, error);
}
  }

  subscribe(channel) {
    if (!this.isConnected) {
      logError('WebSocket not connected, cannot subscribe', { 
        channel,
        isConnected: this.isConnected 
      });
      return;
    }
    
    if (!['stocks', 'flavors', 'all'].includes(channel)) {
      logError('Invalid channel name', { channel });
      return;
    }

    this.socket.send(
      JSON.stringify({
        type: 'subscribe',
        channel,
        timestamp: Date.now()
      })
    );

  }

  unsubscribe(channel) {
    if (!this.isConnected) {
      logError('WebSocket not connected, cannot unsubscribe', { 
        channel,
        isConnected: this.isConnected 
      });
      return;
    }
    
    if (!['stocks', 'flavors', 'all'].includes(channel)) {
      logError('Invalid channel name', { channel });
      return;
    }

    this.socket.send(
      JSON.stringify({
        type: 'unsubscribe',
        channel,
        timestamp: Date.now()
      })
    );

  }

  addHandler(channel, handler) {
    if (typeof handler !== 'function') {
      logError('Handler must be a function', { 
        channel,
        handlerType: typeof handler 
      });
      return;
    }

    if (!this.handlers[channel]) {
      logError('Unknown channel', { channel });
      return;
    }

    if (this.handlers[channel].includes(handler)) {
      return this;
    }

    this.handlers[channel].push(handler);
    return this;
  }

  removeHandler(channel, handler) {
    if (!this.handlers[channel]) {
      logError('Unknown channel', { channel });
      return;
    }

    this.handlers[channel] = this.handlers[channel].filter((h) => h !== handler);
    return this;
  }

  startPingInterval() {
    this.clearPingInterval();
    
    this.pingInterval = setInterval(() => {
      if (this.isConnected) {
        const timeSinceLastMessage = Date.now() - this.lastMessageTime;
        if (timeSinceLastMessage > 60000) {
          logError('No messages received for 60 seconds, reconnecting', { 
            timeSinceLastMessage,
            lastMessageTime: this.lastMessageTime 
          });
          this.disconnect();
          this.connect();
          return;
        }
        
        this.socket.send(
          JSON.stringify({
            type: 'ping',
            timestamp: Date.now(),
          })
        );
      }
    }, 30000);
  }

  clearPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  attemptReconnect() {
    if (!this.connectWithBackoff) {
      return;
    }
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logError('Maximum reconnection attempts reached', { 
        attempts: this.reconnectAttempts,
        maxAttempts: this.maxReconnectAttempts 
      });
      
      window.dispatchEvent(new CustomEvent('websocket:max-attempts', {
        detail: {
          attempts: this.reconnectAttempts,
          timestamp: Date.now()
        }
      }));
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.pow(2, this.reconnectAttempts - 1) * 1000;

 

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  disconnect() {
    if (this.socket) {
      this.clearPingInterval();

      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      
      if (this.connectionTimeoutId) {
        clearTimeout(this.connectionTimeoutId);
        this.connectionTimeoutId = null;
      }

      if (this.isConnected) {
        try {
          this.socket.close(1000, 'Closing connection');
        } catch (e) {
          logError('Error closing WebSocket', {}, e);
        }
      }

      this.socket = null;
      this.isConnected = false;
    }
  }
}


// Data processing utilities with security improvements
export const dataUtils = {
  processFlavorData(flavors) {
    if (!Array.isArray(flavors)) {
      logError('Invalid flavors data format', { 
        flavorsType: typeof flavors,
        flavorsLength: flavors?.length 
      });
      return { gpuTypes: [], rawFlavors: [] };
    }
const gpuTypeMapping = {
  'H100-80G-SXM5': 'H100-SXM5-80GB',
  'H100-80GB': 'H100-SXM5-80GB',
  'H100-80G-PCIe': 'H100-PCIe-80GB',
  'H100-80G-PCIe-NVLink': 'H100-PCIe-NVLink-80GB',
  'A100-80G-PCIe': 'A100-PCIe-80GB',
  'RTX-A6000': 'RTX-A6000',
  'L40': 'L40',
  'B300-262GB': 'B300 SXM6 262GB',
  'B200-180GB': 'B200 SXM6 180GB',
  'H200-141GB': 'H200 SXM5 141GB'
};
    
    const allowedGPUs = Object.keys(gpuTypeMapping);

const filteredFlavors = flavors.filter((item) => {
  if (!item || !item.gpu) return false;
  
  const gpuType = item.gpu;
  const isAllowed = allowedGPUs.includes(gpuType);
  
  
  return isAllowed;
});

const gpuTypes = [
  ...new Set(filteredFlavors.map((item) => item && item.gpu ? item.gpu : null).filter(Boolean)),
].map((gpu) => ({
  id: (gpuTypeMapping[gpu] || gpu).toLowerCase().replace(/\s+/g, '-').replace(/\./g, '-'),
  label: gpuTypeMapping[gpu] || gpu,  // ← FIXED: Shows "B200"
  value: gpu,                         // ← Stays "B200-180GB" for backend
  pricingKey: gpuTypeMapping[gpu]     // ← For pricing lookups
}));

    return {
      gpuTypes,
      rawFlavors: filteredFlavors,
    };
  },
getRegionsFromFlavors(flavors) {
  if (!flavors || !Array.isArray(flavors) || flavors.length === 0) {
    return [];
  }

  const validRegions = flavors.filter((item) => item && (item.region_name || item.region));
  const regions = [...new Set(validRegions.map((item) => item.region_name || item.region))];

    return regions
      .map((region) => {
        const regionStr = region || '';
        return {
          id: regionStr.toLowerCase
            ? regionStr.toLowerCase().replace(/\s+/g, '-')
            : '',
          name: regionStr,
          value: regionStr,
        };
      })
      .filter((region) => region.id);
  },

getGpuCountsForType(flavors, gpuType, region) {
  if (!flavors || !Array.isArray(flavors) || !gpuType || !region) {
    return [];
  }

  const filteredFlavors = flavors.filter(
    (item) => item && item.gpu === gpuType && 
    (item.region_name === region || item.region === region)
  );

  if (filteredFlavors.length === 0) return [];

  const counts = [];

  const flavorGroup = filteredFlavors[0];
  if (flavorGroup && flavorGroup.flavors && Array.isArray(flavorGroup.flavors)) {
    flavorGroup.flavors.forEach((flavor) => {
      if (flavor && typeof flavor.gpu_count === 'number' && flavor.gpu_count > 0) {
        counts.push(flavor.gpu_count);
      }
    });
  } else {
    filteredFlavors.forEach((item) => {
      if (item && typeof item.gpu_count === 'number' && item.gpu_count > 0) {
        counts.push(item.gpu_count);
      }
    });
  }

  return [...new Set(counts)]
    .sort((a, b) => a - b)
    .map((count) => ({ value: count }));
},

 checkAvailability(stocks, gpuType, region, count) {
  if (
    !stocks ||
    !Array.isArray(stocks) ||
    stocks.length === 0 ||
    !gpuType ||
    !region ||
    !count
  ) {
    return false;
  }

const regionStock = stocks.find((item) => item && item.region && item.region.toLowerCase() === region.toLowerCase());
  if (!regionStock || !regionStock.models || !Array.isArray(regionStock.models)) {
    return false;
  }



const getStockModelName = (gpuValue, gpuCount) => {
  if (gpuValue === 'H200-141GB') return `H200-${gpuCount}x`;
  if (gpuValue === 'B300-262GB') return `B300-${gpuCount}x`;  // ← ADD THIS LINE

  if (gpuValue === 'B200-180GB') return `B200-${gpuCount}x`;
  if (gpuValue === 'H100-80GB') return `H100-${gpuCount}x`;
  if (gpuValue === 'H100-SXM5-80GB') return `H100-${gpuCount}x`;
  return gpuValue;
};

const stockModelName = getStockModelName(gpuType, count);

const gpuModel = regionStock.models.find(
  (model) => model && model.model === stockModelName
);
  if (!gpuModel || !gpuModel.configurations) {
    return false;
  }

  const configValue = gpuModel.configurations[`${count}x`];
return (typeof configValue === 'number' && configValue > 0) || 
       (configValue === 'available');
      
      },
  getFlavorDetails(flavors, gpuType, region, count) {
    if (!flavors || !Array.isArray(flavors) || flavors.length === 0 || !gpuType || !region || count === undefined) {
      return null;
    }
    
    let matchingFlavors = flavors.filter(
      item => item && item.gpu === gpuType && item.region_name === region
    );
    
    if (!matchingFlavors || matchingFlavors.length === 0) {
      matchingFlavors = flavors.filter(
        item => item && item.gpu === gpuType && item.region === region
      );
    }
    
    if (!matchingFlavors || matchingFlavors.length === 0) {
      return null;
    }
    
    const numCount = parseInt(count, 10);
    
    let matchingFlavor = matchingFlavors.find(f => f && f.gpu_count === numCount);
    
    if (!matchingFlavor) {
      matchingFlavor = matchingFlavors.find(f => {
        if (!f || !f.name) return false;
        const parts = f.name.split('x');
        if (parts.length < 2) return false;
        const nameCount = parseInt(parts[1], 10);
        return nameCount === numCount;
      });
    }
    
    if (!matchingFlavor) {
      return null;
    }
    
    return {
      id: matchingFlavor.id,
      name: matchingFlavor.name,
      display_name: matchingFlavor.display_name || matchingFlavor.name,
      cpu: matchingFlavor.cpu,
      ram: matchingFlavor.ram,
      disk: matchingFlavor.disk,
      ephemeral: matchingFlavor.ephemeral || 0,
      total_storage: matchingFlavor.disk + (matchingFlavor.ephemeral || 0),
      gpu_count: matchingFlavor.gpu_count,
      features: matchingFlavor.features || {},
      stock_available: matchingFlavor.stock_available
    };
  },

  // Calculate price based on selections
  calculatePrice(flavors, gpuType, region, count, billingPeriod, userCurrency = 'USD') {
    if (!gpuType || !region || !count) {
      return { price: '€0.00/minute', currency: userCurrency };
    }
    
    const numCount = typeof count === 'string' ? parseInt(count, 10) : count;
    if (isNaN(numCount)) {
      return { price: '€0.00/minute', currency: userCurrency };
    }

    try {
     const gpuTypeMapping = {
  'H100-80G-SXM5': 'H100-SXM5-80GB',
    'H100-80GB': 'H100-SXM5-80GB',

  'H100-80G-PCIe': 'H100-PCIe-80GB',
  'H100-80G-PCIe-NVLink': 'H100-PCIe-NVLink-80GB',
  'A100-80G-PCIe': 'A100-PCIe-80GB',
  'A100-80G-PCIe-NVLink': 'A100-SXM4-80GB-NVLink',
  'RTX-A6000': 'RTX-A6000',
  'A40': 'A40',
  'L40': 'L40',
   'B300-262GB': 'B300 SXM6 262GB',
'B200-180GB': 'B200 SXM6 180GB',
  'H200-141GB': 'H200 SXM5 141GB'
};
      

      const standardGpuType = gpuTypeMapping[gpuType] || gpuType;

      if (!VALID_GPUS[standardGpuType]) {
        logError('No pricing found for GPU type', { 
          gpuType,
          standardGpuType,
          availableTypes: Object.keys(VALID_GPUS) 
        });
        return { price: '€0.00/minute', currency: userCurrency };
      }

      const pricePerGpu = VALID_GPUS[standardGpuType].price[userCurrency] || 
                          VALID_GPUS[standardGpuType].price.EUR;
      
      const pricePerMinute = (pricePerGpu * numCount) / 60;
      
      let currencySymbol;
      switch(userCurrency) {
        case 'USD':
          currencySymbol = '$';
          break;
        case 'INR':
          currencySymbol = '₹';
          break;
        case 'EUR':
        default:
          currencySymbol = '€';
          break;
      }
      
      const formattedPrice = `${currencySymbol}${pricePerMinute.toFixed(4)}/minute`;
      
      return { 
        price: formattedPrice, 
        currency: userCurrency 
      };
    } catch (error) {
      logError('Error calculating price', { 
        gpuType,
        region,
        count: numCount,
        userCurrency 
      }, error);
      
      let defaultSymbol = '$';
      if (userCurrency === 'USD') defaultSymbol = '$';
      if (userCurrency === 'INR') defaultSymbol = '₹';
      
      return { price: `${defaultSymbol}0.00/minute`, currency: userCurrency };
    }
  }
};

// Initialize WebSocket service
const wsService = new WebSocketService();

// Export ErrorProcessor for use in components
export { ErrorProcessor };

// Export all services
export default {
  api,
  wsService,
  dataUtils,
};